import { earliestVisibleWave } from '@context/dashboardFetchWave'
import { DashboardSection } from '@constants/Dashboard/dashboard.enums'
import { OVERVIEW_DEFAULT_HIDDEN_SECTIONS } from '@constants/Dashboard/huddleSectionVisibility.constants'

export type OverviewSectionWaves = {
  production: number | null
  procedures: number | null
  collections: number | null
  arAging: number | null
  scheduling: number | null
  clinicalEfficiency: number | null
  caseAcceptance: number | null
  diagnosedTreatment: number | null
  providerUtilization: number | null
  todaySchedule: number | null
  patientFlow: number | null
  incompleteTreatment: number | null
  openTreatment: number | null
  recall: number | null
  chairTime: number | null
  noShowRate: number | null
  sameDayCancellation: number | null
  alerts: number | null
  recare: number | null
  diagnosis: number | null
  treatmentAcceptance: number | null
}

function show(section: DashboardSection, hiddenSections: Set<string>): boolean {
  return (
    !OVERVIEW_DEFAULT_HIDDEN_SECTIONS.has(section) &&
    !hiddenSections.has(section)
  )
}

/** Visible sections to fetch waves. A hidden section is null. */
export function buildOverviewSectionWaves(
  hiddenSections: Set<string>,
): OverviewSectionWaves {
  const visible = (section: DashboardSection): boolean =>
    show(section, hiddenSections)
  return {
    production: earliestVisibleWave([
      [visible(DashboardSection.ProductionTodays), 0],
      [
        visible(DashboardSection.Production) ||
          visible(DashboardSection.TodaysProgress),
        2,
      ],
    ]),
    procedures: visible(DashboardSection.Procedures) ? 1 : null,
    collections: visible(DashboardSection.Collection) ? 2 : null,
    arAging: visible(DashboardSection.ArAging) ? 2 : null,
    scheduling: earliestVisibleWave([
      [
        visible(DashboardSection.Appointments) ||
          visible(DashboardSection.TodaysSchedule),
        0,
      ],
      [visible(DashboardSection.UtilizationMetrics), 1],
      [
        visible(DashboardSection.SameDayCancellation) ||
          visible(DashboardSection.AppointmentOverrun),
        2,
      ],
    ]),
    clinicalEfficiency: earliestVisibleWave([
      [
        visible(DashboardSection.AvgProviderUtilization) ||
          visible(DashboardSection.TreatmentOpportunities),
        0,
      ],
      [visible(DashboardSection.UtilizationMetrics), 1],
    ]),
    caseAcceptance: earliestVisibleWave([
      [visible(DashboardSection.CaseAcceptanceRate), 0],
      [visible(DashboardSection.UtilizationMetrics), 1],
      [
        visible(DashboardSection.CaseAcceptance) ||
          visible(DashboardSection.TreatmentAcceptance),
        2,
      ],
    ]),
    diagnosedTreatment: earliestVisibleWave([
      [visible(DashboardSection.UtilizationMetrics), 1],
      [
        visible(DashboardSection.Diagnosis) ||
          visible(DashboardSection.TreatmentAcceptance) ||
          visible(DashboardSection.CaseAcceptance),
        2,
      ],
    ]),
    providerUtilization: visible(DashboardSection.ProviderUtilization)
      ? 1
      : null,
    todaySchedule: visible(DashboardSection.TodaysSchedule) ? 0 : null,
    patientFlow: visible(DashboardSection.PatientFlowTracker) ? 1 : null,
    incompleteTreatment: visible(DashboardSection.IncompleteTreatmentPlan)
      ? 1
      : null,
    openTreatment: visible(DashboardSection.OpenTreatmentPatients) ? 1 : null,
    recall: visible(DashboardSection.Recall) ? 2 : null,
    chairTime: visible(DashboardSection.ChairTime) ? 2 : null,
    noShowRate: visible(DashboardSection.NoShowRate) ? 2 : null,
    sameDayCancellation: visible(DashboardSection.SameDayCancellation)
      ? 2
      : null,
    alerts: visible(DashboardSection.Alerts) ? 2 : null,
    recare: visible(DashboardSection.Recare) ? 2 : null,
    diagnosis: visible(DashboardSection.Diagnosis) ? 2 : null,
    treatmentAcceptance: visible(DashboardSection.TreatmentAcceptance)
      ? 2
      : null,
  }
}
